import Head from 'next/head';
import '../styles/reset.scss';
import '../styles/globals.scss';
import '../styles/katex.css';
import '../styles/_variables.scss';
import 'public/static/fonts/fonts.css';
import 'react-photo-view/dist/react-photo-view.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-phone-input-2/lib/style.css';
import React, { useEffect, useState } from 'react';
import { RecoilRoot } from 'recoil';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useRouter } from 'next/router';
import { useStudentInfoObjectState, useStudentListState } from '@atoms/states';
import {
  useEventPropertiesStore,
  useMediaWindowInfoStore,
} from '@store/common';
import { Slide } from 'react-toastify';
import { configureScope, Scope } from '@sentry/nextjs';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { queryClientConfig } from 'queries/core/config';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { appWithTranslation } from 'next-i18next';
import nextI18nConfig from '../next-i18next.config';
import { CustomToastContainer } from '@styles/common/toastStyle';
import CheckFillIcon from '@asset/svg/CheckFillIcon';
import CancelFillIcon from '@asset/svg/CancelFillIcon';
import InfoFillIcon from '@asset/svg/InfoFillIcon';
import { useUserInfoQuery } from '@apis/query/user';
import 'swiper/css';
import * as amplitude from '@amplitude/analytics-browser';
import { UserType } from '@asset/enum/UserEnum';
import { useCheckIsIncorrectBuildIdQuery } from '@apis/query/core';
import NotifyUpdateToast from '@components/common/toast/NotifyUpdateToast';
import { GlobalStyle } from '@styles/globalsStyles';
import { Settings } from 'luxon';
import Gnb from '@components/common/nav/Gnb';
import { useWindowSize } from '@public/hooks/useWindowSize';
import '@sendbird/uikit-react/dist/index.css';
import SendbirdWrapper from '@components/chat/ChatRoom';
import {
  useChatPopupStore,
  useChatStore,
  useNotificationStore,
} from '@store/chat';
import { getSendbirdUserInfo } from '@apis/api/chat';
import _ from 'lodash';
import QuestionChatDetailModal from '@components/chat/QuestionChatDetailModal';
import {
  getIsVisibleChatButton,
  registerFCMToSendbird,
  withSendbird,
} from '@asset/function/chat';
import { getDeviceToken } from '@asset/function/notification';
import {
  registerPushToken,
  TokenType,
  unregisterPushToken,
} from '@apis/api/user';
import RndMediaWindow from '@components/common/RndMediaWindow';
import { SENDBIRD_NOTIFICATION_BROADCAST_CHANNEL_NAME } from 'constants/chat';
import { getExternalBrowserRedirectPageUrlObject } from 'routes/account';
import { useBrowser, useDeviceType } from 'hook/checkUserAgent';
import NoticeBanner from '@components/common/NoticeBanner';

dayjs.extend(utc);
dayjs.extend(timezone);

function CheckLoginState({ children }) {
  const { data: user } = useUserInfoQuery();
  const { data: isIncorrectBuildId } = useCheckIsIncorrectBuildIdQuery({
    refetchInterval: 60000,
  });
  const { setUnreadCount } = useChatStore();
  const { notificationPermission, setNotificationPermission } =
    useNotificationStore();

  const router = useRouter();
  const [studentList] = useStudentListState();
  const [, setStudentInfoObject] = useStudentInfoObjectState();
  const [showBrowserNoticeBanner, setShowBrowserNoticeBanner] =
    useState<boolean>(false);
  const [
    showTurnOnChatNotificationNoticeBanner,
    setShowTurnOnChatNotificationNoticeBanner,
  ] = useState<boolean>(false);
  const { isMobileView } = useWindowSize();
  const { isChrome, isSafari, isSamsungBrowser } = useBrowser();
  const { isIpad, isTablet } = useDeviceType();
  const isOtherTablet = !isIpad && isTablet;
  const isErrorPage =
    router.pathname === '/_error' ||
    router.pathname === '/404' ||
    router.pathname === '/500';
  const isAccountPage = router.pathname.includes('account');

  const { data: sendbirdUserInfo } = useQuery(
    ['GET_SENDBIRD_USER_INFO', user?.userId],
    () => getSendbirdUserInfo(user?.userId),
    {
      enabled: !_.isNil(user?.userId),
    },
  );
  const sendbirdUserId = sendbirdUserInfo?.userId;
  const sendbirdAccessToken = sendbirdUserInfo?.accessToken;

  useEffect(() => {
    amplitude.init(
      process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
      user ? String(user.userId) : null,
      {
        minIdLength: 1,
        defaultTracking: { pageViews: true },
      },
    );
    const identifyEvent = new amplitude.Identify();

    if (user) {
      identifyEvent.set('userId', user.userId);
      identifyEvent.set('timezone', user?.timezone);
      identifyEvent.set('language', user?.language);
      identifyEvent.set(
        user.type === UserType.TEACHER ? 'teacherId' : 'studentId',
        user.id,
      );

      configureScope((scope: Scope) => {
        scope.setUser({
          type: user.type,
          id: user.id?.toString(),
          name: user.name,
          timezone: user.timezone,
          language: user.language,
        });
      });
      if (user.type === UserType.STUDENT) Settings.defaultZone = user.timezone;
    }
    amplitude.identify(identifyEvent);
    return () => {
      configureScope((scope: Scope) => scope.clear());
    };
  }, [user]);

  useEffect(() => {
    if (
      !isMobileView &&
      user?.type !== UserType.PARENT &&
      ((isIpad && !isSafari) ||
        (isOtherTablet && !isChrome) ||
        isSamsungBrowser)
    ) {
      setShowBrowserNoticeBanner(true);
    }
  }, [
    user,
    isIpad,
    isOtherTablet,
    isChrome,
    isSafari,
    isSamsungBrowser,
    isMobileView,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
    if (
      user?.type === UserType.STUDENT &&
      !_.isNil(sendbirdUserInfo?.currentClassChannelUrl)
    ) {
      setShowTurnOnChatNotificationNoticeBanner(
        notificationPermission !== 'granted',
      );
    } else {
      setShowTurnOnChatNotificationNoticeBanner(false);
    }
  }, [user, sendbirdUserInfo, notificationPermission]);

  useEffect(() => {
    const _studentInfoObject = {};
    if (studentList) {
      if (studentList.myStudentList || studentList.otherStudentList) {
        studentList.myStudentList?.map(
          (studentInfo) =>
            (_studentInfoObject[`${studentInfo.id}`] = studentInfo),
        );
        studentList.otherStudentList?.map(
          (studentInfo) =>
            (_studentInfoObject[`${studentInfo.id}`] = studentInfo),
        );
        setStudentInfoObject(_studentInfoObject);
      }
    }
  }, [studentList]);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/kakaotalk/i.test(userAgent)) {
      router.push(
        getExternalBrowserRedirectPageUrlObject({
          redirectUrl: window.location.href,
        }),
      );
    }
  }, []);

  const setUpSendbird = async (
    sendbirdUserId: string,
    sendbirdAccessToken: string,
  ) => {
    const currentToken = await getDeviceToken();
    await withSendbird(sendbirdUserId, sendbirdAccessToken, async (sb) => {
      if (!_.isNil(sb.groupChannel)) {
        const unread = await sb.groupChannel.getTotalUnreadMessageCount();
        setUnreadCount(unread);
      }
      if (currentToken) {
        await registerFCMToSendbird(sb, currentToken);
      }
    });
  };

  useEffect(() => {
    if (sendbirdUserId && sendbirdAccessToken) {
      setUpSendbird(sendbirdUserId, sendbirdAccessToken);
    }
  }, [user, sendbirdUserId, sendbirdAccessToken]);

  useEffect(() => {
    if (user) {
      getDeviceToken().then(async (token) => {
        if (token) {
          await unregisterPushToken(user.userId);
          registerPushToken(user.userId, TokenType.FCM, token);
        }
      });
    }
  }, [user]);

  const isVisibleChatButton = getIsVisibleChatButton(
    sendbirdUserInfo,
    user?.type,
    user?.language,
  );

  return (
    <>
      {isIncorrectBuildId && <NotifyUpdateToast />}
      <section
        className="flex flex-col"
        style={{
          height: '100dvh',
        }}
      >
        {showBrowserNoticeBanner && (
          <NoticeBanner
            content={`올마이티캠퍼스는 ${
              isIpad ? 'Safari' : 'Chrome'
            } 환경에 최적화 되어있습니다. ${
              isIpad ? 'Safari' : 'Chrome'
            } 브라우저로 접속해 주세요.`}
            onClose={() => setShowBrowserNoticeBanner(false)}
          />
        )}
        {showTurnOnChatNotificationNoticeBanner && (
          <NoticeBanner
            content={
              <>
                채팅 알림이 꺼져있어요. 원활한 채팅을 위해 채팅 알림을 허용해
                주세요.
                <button
                  className="text-my-blue-50 text-label-lg-700"
                  onClick={() =>
                    window.open(
                      'https://almighty-campus.notion.site/19668fac541a80aba520c2112ea513a7',
                    )
                  }
                >
                  허용방법 알아보기
                </button>
              </>
            }
            onClose={() => setShowTurnOnChatNotificationNoticeBanner(false)}
          />
        )}
        {isAccountPage ||
        isErrorPage ||
        router.pathname.includes('study-report') ||
        router.pathname.includes('problem-solving') ||
        router.pathname.includes('/feedback/detail') ||
        isMobileView ? (
          <></>
        ) : (
          <Gnb isVisibleChatButton={isVisibleChatButton} />
        )}
        <div className="relative flex-1 overflow-auto">{children}</div>
      </section>
    </>
  );
}

function MainComponent({ Component, pageProps }) {
  const router = useRouter();
  if (router.pathname.startsWith('/account/signup/complete')) {
    return <Component {...pageProps} />;
  }
  return (
    <CheckLoginState>
      <SendbirdWrapper />
      <QuestionChatDetailModal />
      <Component {...pageProps} />
    </CheckLoginState>
  );
}

function MyApp({ Component, pageProps }) {
  const [queryClient] = useState(() => {
    return new QueryClient(queryClientConfig);
  });
  const router = useRouter();
  const { setProperties } = useEventPropertiesStore();
  const { mediaInfoList, saveWindowPosition, saveWindowSize, hideMediaWindow } =
    useMediaWindowInfoStore();
  const { selectedChannel, setUnreadCount, setClickedChatNotification } =
    useChatStore();
  const { setIsChatPopupOpen } = useChatPopupStore();

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(
      SENDBIRD_NOTIFICATION_BROADCAST_CHANNEL_NAME,
    );

    const onMessage = (event: MessageEvent) => {
      if (event.data.action === 'push') {
        const pushChannelUrl = event.data.data.sendbird?.channel?.channel_url;
        if (selectedChannel?.url && selectedChannel.url !== pushChannelUrl) {
          setUnreadCount(
            event.data.data.sendbird?.channel?.channel_unread_message_count,
          );
        }
      }
    };

    broadcastChannel?.addEventListener('message', onMessage);
    return () => {
      broadcastChannel?.removeEventListener('message', onMessage);
      broadcastChannel?.close();
    };
  }, [selectedChannel?.url]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data.action === 'notificationClick') {
        const clickedChannelUrl =
          event.data.data.sendbird?.channel?.channel_url ||
          event.data.data.content?.student?.sendbirdChannelUrl;
        if (!_.isNil(clickedChannelUrl)) {
          setClickedChatNotification({ channelUrl: clickedChannelUrl });
          setIsChatPopupOpen(true);
        }
      }
    };

    navigator?.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      navigator?.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    const chartTooltip = document.querySelector('div#chartjs-tooltip');
    if (chartTooltip) {
      chartTooltip.remove();
    }

    setProperties({
      url: typeof window !== 'undefined' ? window.location.href : '',
      path: router.asPath,
    });
  }, [router.asPath]);

  return (
    <>
      <GlobalStyle />
      <QueryClientProvider client={queryClient}>
        <RecoilRoot>
          <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_CLIENT_ID}>
            <Head>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0"
              />
              <link rel="manifest" href="/manifest.json" />
              <link
                rel="shortcut icon"
                href="/common/logo/favicon.svg"
                type="image/x-icon"
              />
            </Head>
            <main>
              <MainComponent Component={Component} pageProps={pageProps} />
              <CustomToastContainer
                position="top-center"
                autoClose={2500}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={false}
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
                icon={({ type }) => {
                  if (type === 'success') {
                    return <CheckFillIcon />;
                  } else if (type === 'info') {
                    return <CancelFillIcon />;
                  } else if (type === 'error') {
                    return <InfoFillIcon />;
                  }
                }}
              />
            </main>
            {mediaInfoList.map((media) =>
              media.open ? (
                <RndMediaWindow
                  key={media.key}
                  title={media.title}
                  mediaType={media.type}
                  mediaUrl={media.url}
                  onClose={() => {
                    hideMediaWindow(media.key);
                  }}
                  size={media.size}
                  position={media.position}
                  onDragStop={(changedPosition) => {
                    saveWindowPosition(media.key, changedPosition);
                  }}
                  onResizeStop={(changedSize, changedPosition) => {
                    saveWindowSize(media.key, changedSize);
                    saveWindowPosition(media.key, changedPosition);
                  }}
                />
              ) : (
                <></>
              ),
            )}
            <style jsx>{`
              body {
                > div {
                  display: flex;
                  flex-direction: column;
                }
              }
            `}</style>
          </GoogleOAuthProvider>
        </RecoilRoot>
      </QueryClientProvider>
    </>
  );
}

export default appWithTranslation(MyApp, nextI18nConfig);
